// パレット（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { catLabel, catShort, txt, txtHtml } from './text.js';
import { catByKey } from './store.js';
import { keyOf } from './util.js';
import { hexToRgb } from './colormath.js';
import { PTN_ADJUST, PTN_EX1, PTN_EX2, PTN_SIZE, PTN_STROKE1, PTN_STROKE2, loadPatterns } from './core/pattern.js';
import { el } from './core/util.js';
import type { PatternItem } from './core/model.js';
import { categoryIcon } from './categories.js';
import { applyGrad } from './colors.js';
import { defaultLinks, familyCats, groupOf, linkFamily, loadLinks, saveLinks } from './colorlink.js';
import { lrOn, buildColors } from './lr.js';
import { histCut } from './history.js';
import { openModalNode } from './dialog.js';
import type { Category } from './types.js';
import { setColor as setColorTo, setPattern as setPatternTo, setPatternSwap, setSideColor } from './core/ops.js';
import { CONFIG } from './config.js';
import { partsList } from './core/data.js';
import { choosePart } from './parts.js';

// ---- パレット ----
// 本物のパレット（data.PALLET、104 色）は黄色から始まり、肌に使う色も2列目にばらばらにある。モックは色の段に1つのパレットだけを出し、
// 「色の設定」（パレットの名前のボタン → ダイアログ）で切りかえる（ユーザー「横にずらっと並ぶと使いにくい。連動の設定と一緒にモーダルで」2026-10-11）:
//   suggest: カテゴリに合ったおすすめ（肌・髪・目。SUGGEST） / recent: 最近使った色（このブラウザ、10 色まで） /
//   chara: このキャラで使っている色（ほかのパーツと色をそろえやすく） / all: パレットの全部を、灰色（明るい順）→ 色合い
//   （赤 → 黄 → 緑 → 青 → 紫 → ピンク）・明るい順 に並べ直したもの
// どれを出すかは、カテゴリの種類（肌・髪・目・そのほか）ごとにこのブラウザに残す（charatCreate.palette）。はじめはおすすめ（無ければ全部）。
// ほかに、色の段の先頭に「自由な色」（パレットに無い色。本物でもそのまま出る・保存できる。2026-10-11 に確認）
export const PAL_KEY = 'charatCreate.palette', PAL_IDS = ['suggest', 'recent', 'chara', 'all'];
// 柄の一覧の見本（1枚分の柄を、キャラと同じ makePattern で色を付けて画像に。今の色だけ取っておく）。
// 色は計算後の値で見るので、ページの中の見えない svg の defs で作る（2026-10-23 に pattern.ts から。柄そのものは中身の core/pattern.ts）
export function patternSwatch(p: PatternItem, ground: string, rev: number) {
  if (ground + rev !== S.swatchGround) { S.swatchCache = {}; S.swatchGround = ground + rev; }
  if (!S.swatchCache[p.id]) {
    if (!S.swatchDefs) {
      const box = el('svg', { width: 0, height: 0, 'aria-hidden': 'true', focusable: 'false' });
      box.style.position = 'absolute';
      box.style.left = '-9999px';
      S.swatchDefs = el('defs');
      box.appendChild(S.swatchDefs);
      document.body.appendChild(box);
    }
    charatDraw.makePattern(S.swatchDefs, p.g, 'ptnSwatch', rev ? rev : undefined, ground, PTN_EX1, PTN_EX2, PTN_STROKE1, PTN_STROKE2, false, PTN_SIZE, PTN_ADJUST);
    const g = S.swatchDefs.querySelector('#ptnSwatch > g > g');
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="' + -PTN_ADJUST + ' ' + -PTN_ADJUST + ' ' + PTN_SIZE + ' ' + PTN_SIZE + '">' +
      new XMLSerializer().serializeToString(g!) + '</svg>';
    S.swatchCache[p.id] = 'url("data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg) + '")';
  }
  return S.swatchCache[p.id];
}
export function paletteKind(cat: Category): string { return suggestKind(cat) || 'other'; }
export function palettesFor(kind: string) { return PAL_IDS.filter((id) => id !== 'suggest' || !!SUGGEST[kind]); }
export function paletteOf(kind: string): string {
  let list = palettesFor(kind), id = null;
  try { id = (JSON.parse(localStorage.getItem(PAL_KEY) || '{}') || {})[kind]; } catch (e) { /* はじめのまま */ }
  return list.indexOf(id) >= 0 ? id : list[0] === 'suggest' ? 'suggest' : 'all';
}
export function setPalette(kind: string, id: string) {
  let o: Record<string, string> = {};
  try { o = JSON.parse(localStorage.getItem(PAL_KEY) || '{}') || {}; } catch (e) { o = {}; }
  o[kind] = id;
  try { localStorage.setItem(PAL_KEY, JSON.stringify(o)); } catch (e) { /* 残せなくても動く */ }
}
export function paletteName(kind: string, id: string) { return txt(id === 'suggest' ? 'pal_suggest_' + kind : 'pal_' + id); }
export function paletteColors(cat: Category, id: string): string[] {
  if (id === 'suggest') return SUGGEST[paletteKind(cat)] || [];
  if (id === 'recent') return recentColors();
  if (id === 'chara') return charaColors(cat);
  return sortedPalette();
}
export const SUGGEST: Record<string, string[]> = {
  skin: ['#fff4ec', '#ffe8d9', '#fde0cf', '#f9d3bf', '#f5c6a5', '#eab38f', '#dca07a', '#c98a63', '#b0714c', '#8d5524', '#6e4024', '#4a2c1d', '#e8eef7', '#d7e9d2', '#c9c3e6', '#9fb4c7'],
  hair: ['#1b1b1f', '#3b2a24', '#5a3825', '#7b4a2d', '#a26a42', '#c99a5b', '#e8c77e', '#f3e2b0', '#ece9e2', '#b8bcc6', '#8a1f22', '#c8432f', '#f08aa8', '#b48be0', '#4d7fd6', '#3fa38f'],
  eye: ['#5a3a22', '#8a5a2b', '#c9902c', '#3e8e4e', '#2f7fbf', '#8fb8d8', '#7a8794', '#b3262e', '#8b4fc0', '#e06b9a', '#e2b93b', '#2b2b33']
};
export function suggestKind(cat: Category) {
  const fam = linkFamily(cat);
  if (fam) return fam;
  return keyOf(cat) === 'eye' ? 'eye' : null;
}
export const RECENT_KEY = 'charatCreate.recentColors', RECENT_MAX = 10;
export function recentColors() {
  try { const a = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(a) ? a.filter((h) => /^#[0-9a-f]{6}$/.test(h)).slice(0, RECENT_MAX) : []; } catch (e) { return []; }
}
export function rememberColor(hex: string) {
  if (!/^#[0-9a-f]{6}$/.test(hex)) return;
  const a = recentColors().filter((h) => h !== hex);
  a.unshift(hex);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(a.slice(0, RECENT_MAX))); } catch (e) { /* 残せなくても動く */ }
}
// このキャラで使っている色（選んでいるカテゴリの色は除く）
export function charaColors(cat: Category) {
  const mine = keyOf(cat), seen: Record<string, boolean> = {}, out: string[] = [];
  C.cats.forEach((c) => {
    const st = C.state[keyOf(c)];
    if (!st || keyOf(c) === mine || st.parts == null) return;
    (['col', 'from', 'to', 'lfrom', 'rfrom'] as const).forEach((f) => {
      const v = String(st[f] || '').toLowerCase();
      if (/^#[0-9a-f]{6}$/.test(v) && !seen[v]) { seen[v] = true; out.push(v); }
    });
  });
  return out.slice(0, 12);
}
export function sortedPalette() {
  if (S.paletteSorted) return S.paletteSorted;
  const list: { hex: string; gray: boolean; hue: number; l: number }[] = [], seen: Record<string, boolean> = {};
  C.data.PALLET.forEach((row) => {
    const hex = String(row[0]).toLowerCase();
    if (seen[hex]) return;
    seen[hex] = true;
    const c = hexToRgb(hex).map((v) => v / 255);
    const max = Math.max.apply(null, c), min = Math.min.apply(null, c), l = (max + min) / 2, d = max - min;
    const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    let hue = 0;
    if (d) {
      if (max === c[0]) hue = ((c[1] - c[2]) / d + 6) % 6;
      else if (max === c[1]) hue = (c[2] - c[0]) / d + 2;
      else hue = (c[0] - c[1]) / d + 4;
      hue *= 60;
    }
    if (hue >= 345) hue -= 360; // 赤みのピンクも赤の並びに
    list.push({ hex: hex, gray: sat < 0.12 || d < 0.06, hue: hue, l: l });
  });
  list.sort((a, b) => {
    if (a.gray !== b.gray) return a.gray ? -1 : 1;
    if (a.gray) return b.l - a.l;
    const ha = Math.floor((a.hue + 15) / 30), hb = Math.floor((b.hue + 15) / 30); // 30 度ずつの色合いの組で、組の中は明るい順
    return ha !== hb ? ha - hb : b.l - a.l;
  });
  return (S.paletteSorted = list.map((x) => x.hex));
}
// 色の設定: パレットの切りかえ（見本の丸つき）と、色の連動（髪とまゆ・肌だけ。同じ塗り方のカテゴリの中で入れ外し）。
// 選ぶとすぐ色の段に出る（ダイアログは後ろを薄く暗くするだけで、キャラと色の段が見えたまま）
export function openColorSettings(cat: Category) {
  var kind = paletteKind(cat), fam = linkFamily(cat);
  const root = document.createElement('div');
  root.className = 'rnd colorset';
  root.innerHTML = '<h2>' + txtHtml('color_settings') + '</h2><h3>' + txtHtml('pal_section') + '</h3><div class="pal-options" role="radiogroup" aria-label="' + txtHtml('pal_section') + '"></div>' +
    (fam ? '<h3>' + txtHtml('link_title') + ' <button type="button" class="text-btn link-reset">' + txtHtml('link_reset') + '</button></h3><p class="note">' +
      txtHtml(fam === 'hair' ? 'link_note_hair' : 'link_note_skin') + '</p><div class="lock-grid"></div>' : '');
  var opts = root.querySelector('.pal-options')!;
  palettesFor(kind).forEach((id) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'pal-opt';
    b.dataset.id = id;
    b.setAttribute('role', 'radio');
    const name = document.createElement('span');
    name.className = 'pal-name';
    name.textContent = paletteName(kind, id);
    b.appendChild(name);
    const dots = document.createElement('span');
    dots.className = 'pal-dots';
    const hexes = paletteColors(cat, id);
    if (!hexes.length) dots.textContent = txt('pal_empty');
    hexes.slice(0, 10).forEach((hex) => { const i = document.createElement('i'); i.style.background = hex; dots.appendChild(i); });
    b.appendChild(dots);
    b.addEventListener('click', () => { setPalette(kind, id); draw(); buildColors(catByKey[S.selKey]); });
    opts.appendChild(b);
  });
  var grid = root.querySelector('.lock-grid');
  if (grid) {
    familyCats(fam).forEach((c) => {
      const key = keyOf(c);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lock-cat' + (key === keyOf(cat) ? ' now' : '');
      b.dataset.key = key;
      b.title = catLabel(key);
      b.appendChild(categoryIcon(c));
      const t = document.createElement('span');
      t.textContent = catShort(key);
      b.appendChild(t);
      b.addEventListener('click', () => {
        const set = loadLinks()[fam!], i = set.indexOf(key);
        if (i >= 0) set.splice(i, 1); else set.push(key);
        saveLinks();
        draw();
        buildColors(catByKey[S.selKey]);
      });
      grid!.appendChild(b);
    });
    root.querySelector('.link-reset')!.addEventListener('click', () => { S.links![fam!] = defaultLinks()[fam!]; saveLinks(); draw(); buildColors(catByKey[S.selKey]); });
  }
  function draw() {
    const pal = paletteOf(kind);
    opts.querySelectorAll<HTMLElement>('.pal-opt').forEach((b) => {
      const on = b.dataset.id === pal;
      b.classList.toggle('on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    if (!grid) return;
    const set = loadLinks()[fam!];
    grid.querySelectorAll<HTMLElement>('.lock-cat').forEach((b) => {
      const on = set.indexOf(b.dataset.key!) >= 0;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  draw();
  openModalNode(root, [], { light: true });
  if (grid) grid.querySelectorAll('.lock-cat span').forEach((t) => { if (t.scrollWidth > t.clientWidth) t.classList.add('tight'); });
}
// 柄の一覧（先頭は「柄なし」= 今の色だけの丸。柄はパーツの今の色で見せる）
export function buildPatterns(cat: Category, box: HTMLElement) {
  const st = C.state[keyOf(cat)];
  const ground = st.col || cat.COLOR[0].toLowerCase();
  const none = document.createElement('button');
  none.type = 'button';
  none.className = 'sw' + (!st.ptn ? ' sel' : '');
  none.style.background = ground;
  none.title = txt('pattern_none');
  none.setAttribute('aria-label', txt('pattern_none'));
  none.addEventListener('click', () => { setPattern(''); });
  box.appendChild(none);
  if (!C.patterns) {
    const wait = document.createElement('span');
    wait.className = 'ptn-wait';
    wait.textContent = txt('loading');
    box.appendChild(wait);
    loadPatterns().then(() => { if (S.selKey === keyOf(cat) && S.colorTab === 'pattern') buildColors(cat); }, () => { wait.textContent = txt('load_failed'); });
    return;
  }
  // 色の入れかえ（柄が付いているときだけ。見本も入れかえた色で）
  const rev = st.ptn ? charatDraw.patternRev(Number(st.prev) || 0, Number(st.ptnmode) || 0) : (Number(st.prev) || 0);
  if (st.ptn) {
    const swap = document.createElement('button');
    swap.type = 'button';
    swap.className = 'pal-chip ptn-swap' + (Number(st.prev) ? ' on' : '');
    swap.title = txt('pattern_swap_title');
    swap.setAttribute('aria-pressed', Number(st.prev) ? 'true' : 'false');
    swap.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4 3.5 7.5 7 11M3.5 7.5H15a4 4 0 0 1 4 4M17 20l3.5-3.5L17 13M20.5 16.5H9a4 4 0 0 1-4-4"/></svg><span>' + txtHtml('pattern_swap') + '</span>';
    swap.addEventListener('click', swapPatternColors);
    box.insertBefore(swap, none);
  }
  C.patterns.forEach((p) => {
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'sw ptn' + (st.ptn === p.id ? ' sel' : '');
    sw.style.backgroundImage = patternSwatch(p, ground, rev);
    sw.title = txt('pattern_n', { n: Number(p.id) });
    sw.setAttribute('aria-label', sw.title);
    sw.addEventListener('click', () => { setPattern(p.id); });
    box.appendChild(sw);
  });
}
// 柄を付ける・外す（グループのカテゴリにまとめて。本物の setPattern・clearPattern と同じく、初めて付けるときはそのときの色反転 prev を ptnmode に、外すと両方 0）
export function setPattern(id: string) {
  const cat = catByKey[S.selKey];
  if (!cat || !C.state[S.selKey]) return;
  histCut();
  setPatternTo(groupOf(cat), id);
  buildColors(cat);
}
// 柄の色の入れかえ（本物の btnRevColor の柄があるとき: 選んでいるカテゴリの prev を 0 ⇔ 1（MODE_COLREV 1）にして、グループの柄を作り直す）
export function swapPatternColors() {
  const cat = catByKey[S.selKey], st0 = C.state[S.selKey];
  if (!cat || !st0 || !st0.ptn) return;
  histCut();
  const prev = Number(st0.prev) ? 0 : 1;
  setPatternSwap(groupOf(cat), prev);
  buildColors(cat);
}
// quiet: 一覧を描き直さない（自由な色の部品を動かしている間。描き直すと部品が消える）
// key: 塗るカテゴリ（既定は選んでいるカテゴリ。「ふく」の色の欄の「リボン」＝設定 subTabs は、選んでいるのが ふく のままリボンのカテゴリに塗る。2026-10-29）
export function setColor(hex: string, quiet?: boolean, key: string = S.selKey) {
  const cat = catByKey[key];
  // インナーカラー・スカートの裏地（設定 autoOn）: 「なし」・元の色のまま（まだ選んでいないときも）色を選んだら、先に付けて見えるように
  // （2026-10-28 ユーザー。ON/OFF が分かりにくかった）
  const st0 = C.state[key];
  if (cat && hex && !quiet && CONFIG.autoOn && CONFIG.autoOn.indexOf(key) >= 0 && (!st0 || Number(st0.parts) === 1)) {
    const on = partsList(cat.NAME).filter((p) => Number(p.id) === 2)[0];
    if (on) choosePart(cat, on, partsList(cat.NAME));
  }
  if (!cat || !C.state[key]) return;
  if (!quiet) histCut();
  if (hex && !quiet) rememberColor(hex);
  if (cat.GRAD.length > 1 && S.grad && S.grad.key === key) {
    if (hex === '') {
      // 元の色: パーツ本来のグラデーションに（本物の selGrad の「元の色」と同じく、上 = GRAD[1]・下 = GRAD[0] を入れる）
      S.grad.from = cat.GRAD[1].toLowerCase(); S.grad.to = cat.GRAD[0].toLowerCase();
    } else {
      if (!S.grad.sel.from && !S.grad.sel.to) S.grad.sel.from = true; // どちらも選んでいなければ上に
      if (S.grad.sel.from) S.grad.from = hex;
      if (S.grad.sel.to) S.grad.to = hex;
    }
    applyGrad();
  } else if (lrOn(cat) && S.lrSel && key === S.selKey) {
    // 左・右だけに付ける。本物は全体の色（col）があるときだけ左右の色を読み直すので、無ければ元の色を入れておく（見た目は同じ）
    setSideColor(groupOf(cat), S.lrSel, hex);
  } else {
    // 両方に付けると、左・右だけの色は消える（本物と同じ）
    setColorTo(groupOf(cat), hex);
  }
  if (!quiet && catByKey[S.selKey]) buildColors(catByKey[S.selKey]);
}
